import React, { useState, useEffect } from 'react';
import { 
  practiceCategories, 
  testForms, 
  getRandomQuestions,
  calculateScaledScore,
  stats
} from './data/index.js';

// ============================================================================
// ICONS (Simple SVG components)
// ============================================================================
const Icons = {
  Target: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2"/><circle cx="12" cy="12" r="6" strokeWidth="2"/><circle cx="12" cy="12" r="2" strokeWidth="2"/></svg>,
  Trophy: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M6 9H4.5a2.5 2.5 0 010-5H6m12 5h1.5a2.5 2.5 0 000-5H18M6 9v6a6 6 0 0012 0V9M6 9h12M9 21h6m-3-6v6"/></svg>,
  BookOpen: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M12 6.25V19.25M12 6.25C10.83 5.48 9.25 5 7.5 5C5.75 5 4.17 5.48 3 6.25V19.25C4.17 18.48 5.75 18 7.5 18C9.25 18 10.83 18.48 12 19.25M12 6.25C13.17 5.48 14.75 5 16.5 5C18.25 5 19.83 5.48 21 6.25V19.25C19.83 18.48 18.25 18 16.5 18C14.75 18 13.17 18.48 12 19.25"/></svg>,
  Clock: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" strokeWidth="2"/><path strokeWidth="2" d="M12 6v6l4 2"/></svg>,
  Check: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M5 13l4 4L19 7"/></svg>,
  X: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>,
  ChevronRight: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M9 5l7 7-7 7"/></svg>,
  ChevronLeft: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M15 19l-7-7 7-7"/></svg>,
  LogOut: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>,
  Zap: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>,
  Play: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M5 3l14 9-14 9V3z"/></svg>,
  BarChart: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M12 20V10m6 10V4M6 20v-4"/></svg>,
  Shuffle: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/></svg>,
  TrendingUp: () => <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M23 6l-9.5 9.5-5-5L1 18"/></svg>,
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================
const MasteryBar = ({ percentage }) => {
  const getColor = (pct) => {
    if (pct >= 80) return 'bg-emerald-500';
    if (pct >= 50) return 'bg-amber-500';
    return 'bg-rose-500';
  };
  return (
    <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
      <div className={`h-full ${getColor(percentage)} transition-all duration-500`} style={{ width: `${percentage}%` }} />
    </div>
  );
};

const ScoreRing = ({ score, max = 36, size = 80 }) => {
  const percentage = (score / max) * 100;
  const circumference = 2 * Math.PI * 32;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle cx={size/2} cy={size/2} r="32" fill="none" stroke="#334155" strokeWidth="6"/>
        <circle cx={size/2} cy={size/2} r="32" fill="none" stroke="#f59e0b" strokeWidth="6" strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{ transition: 'stroke-dashoffset 1s ease-out' }}/>
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-white">{score}</span>
      </div>
    </div>
  );
};

// ============================================================================
// LOGIN SCREEN
// ============================================================================
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState('');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg">
              <div className="w-7 h-7 text-slate-900"><Icons.Target /></div>
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">ACT Prep</span>
          </div>
          <p className="text-slate-400">Master the test. Reach your goals.</p>
          <p className="text-amber-400 text-sm mt-2">{stats.totalQuestions.toLocaleString()} practice questions ready</p>
        </div>
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-8">
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="student@example.com" 
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <input type="password" placeholder="••••••••" className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"/>
            </div>
            <button 
              onClick={() => onLogin({ name: email.split('@')[0] || 'Student', email: email || 'student@example.com' })} 
              className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900 font-semibold rounded-xl shadow-lg transition-all"
            >
              Sign In
            </button>
          </div>
          <p className="mt-6 text-center text-slate-500 text-sm">Demo mode: Click to continue</p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HEADER
// ============================================================================
const Header = ({ user, activeTab, setActiveTab, onLogout }) => (
  <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
    <div className="max-w-6xl mx-auto px-4">
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
            <div className="w-5 h-5 text-slate-900"><Icons.Target /></div>
          </div>
          <span className="text-xl font-bold text-white">ACT Prep</span>
        </div>
        <div className="flex items-center bg-slate-800 rounded-xl p-1">
          <button onClick={() => setActiveTab('testing')} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'testing' ? 'bg-amber-500 text-slate-900' : 'text-slate-400 hover:text-white'}`}>
            <div className="w-4 h-4"><Icons.Trophy /></div>Testing
          </button>
          <button onClick={() => setActiveTab('practice')} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'practice' ? 'bg-amber-500 text-slate-900' : 'text-slate-400 hover:text-white'}`}>
            <div className="w-4 h-4"><Icons.BookOpen /></div>Practice
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-white capitalize">{user.name}</p>
            <p className="text-xs text-slate-400">Goal: 28 ACT Score</p>
          </div>
          <button onClick={onLogout} className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800">
            <div className="w-5 h-5"><Icons.LogOut /></div>
          </button>
        </div>
      </div>
    </div>
  </header>
);

// ============================================================================
// TESTING DASHBOARD
// ============================================================================
const TestingDashboard = ({ testHistory, onStartTest }) => {
  const completedTests = testHistory || [];
  const availableTests = testForms.filter(t => !completedTests.find(ct => ct.formId === t.id));
  const avgScore = completedTests.length 
    ? Math.round(completedTests.reduce((sum, t) => sum + t.scaledScore, 0) / completedTests.length)
    : null;

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Current Score</p>
              <p className="text-3xl font-bold text-white mt-1">{avgScore || '--'}</p>
            </div>
            {avgScore && <ScoreRing score={avgScore} />}
          </div>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Goal Score</p>
              <p className="text-3xl font-bold text-amber-400 mt-1">28</p>
            </div>
            <div className="w-16 h-16 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <div className="w-8 h-8 text-amber-400"><Icons.Target /></div>
            </div>
          </div>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Tests Completed</p>
              <p className="text-3xl font-bold text-white mt-1">{completedTests.length} / {testForms.length}</p>
            </div>
            <div className="w-16 h-16 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <div className="w-8 h-8 text-emerald-400"><Icons.Check /></div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <div className="w-5 h-5 text-amber-400"><Icons.Play /></div>Available Tests
        </h2>
        <div className="grid grid-cols-3 gap-4">
          {availableTests.map((test) => (
            <div key={test.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 hover:border-amber-500/50 transition-all cursor-pointer group" onClick={() => onStartTest(test)}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-white">{test.name}</h3>
                  <p className="text-sm text-slate-400">{test.format}</p>
                </div>
                <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-lg">Available</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1"><div className="w-4 h-4"><Icons.BookOpen /></div>{test.questions} Qs</span>
                <span className="flex items-center gap-1"><div className="w-4 h-4"><Icons.Clock /></div>{test.duration} min</span>
              </div>
              <button className="mt-4 w-full py-2 bg-amber-500/10 text-amber-400 font-medium rounded-lg group-hover:bg-amber-500 group-hover:text-slate-900 transition-all flex items-center justify-center gap-2">
                Start Test <div className="w-4 h-4"><Icons.ChevronRight /></div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {completedTests.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <div className="w-5 h-5 text-amber-400"><Icons.Trophy /></div>Completed Tests
          </h2>
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-5 py-3 text-sm font-medium text-slate-400">Test</th>
                  <th className="text-left px-5 py-3 text-sm font-medium text-slate-400">Date</th>
                  <th className="text-center px-5 py-3 text-sm font-medium text-slate-400">Raw</th>
                  <th className="text-center px-5 py-3 text-sm font-medium text-slate-400">Scaled</th>
                </tr>
              </thead>
              <tbody>
                {completedTests.map((test, idx) => (
                  <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className="px-5 py-4 font-medium text-white">{test.formName}</td>
                    <td className="px-5 py-4 text-slate-400">{new Date(test.date).toLocaleDateString()}</td>
                    <td className="px-5 py-4 text-center text-slate-300">{test.rawScore}/{test.totalScored}</td>
                    <td className="px-5 py-4 text-center">
                      <span className="inline-flex items-center justify-center w-10 h-10 bg-amber-500/20 text-amber-400 font-bold rounded-lg">{test.scaledScore}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// PRACTICE DASHBOARD
// ============================================================================
const PracticeDashboard = ({ practiceHistory, onStartPractice }) => {
  const getCategoryMastery = (categoryId) => {
    const categoryResults = practiceHistory.filter(r => r.category === categoryId);
    if (categoryResults.length === 0) return 0;
    const correct = categoryResults.filter(r => r.correct).length;
    return Math.round((correct / categoryResults.length) * 100);
  };

  const categoriesWithMastery = practiceCategories.map(cat => ({
    ...cat,
    mastery: getCategoryMastery(cat.id),
    attempted: practiceHistory.filter(r => r.category === cat.id).length,
    count: cat.questions.length
  }));

  const overallMastery = practiceHistory.length > 0
    ? Math.round((practiceHistory.filter(r => r.correct).length / practiceHistory.length) * 100)
    : 0;

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-slate-800 to-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white mb-1">Overall Mastery</h2>
            <p className="text-slate-400">Keep practicing to reach 80% in all categories</p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold text-amber-400">{overallMastery}%</p>
            <p className="text-sm text-slate-400">{stats.totalQuestions.toLocaleString()} questions available</p>
          </div>
        </div>
        <div className="mt-4"><MasteryBar percentage={overallMastery} /></div>
        <p className="text-sm text-slate-500 mt-2">{practiceHistory.length} questions attempted</p>
      </div>

      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <div className="w-5 h-5 text-amber-400"><Icons.BarChart /></div>Practice by Category
        </h2>
        <div className="grid grid-cols-3 gap-4">
          {categoriesWithMastery.map((category) => (
            <div key={category.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 hover:border-amber-500/50 transition-all cursor-pointer group" onClick={() => onStartPractice(category, 10)}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{category.icon}</span>
                  <div>
                    <h3 className="font-semibold text-white">{category.name}</h3>
                    <p className="text-sm text-slate-400">{category.count} questions</p>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Mastery</span>
                  <span className={`font-medium ${category.mastery >= 80 ? 'text-emerald-400' : category.mastery >= 50 ? 'text-amber-400' : 'text-rose-400'}`}>{category.mastery}%</span>
                </div>
                <MasteryBar percentage={category.mastery} />
                <p className="text-xs text-slate-500">{category.attempted} attempted</p>
              </div>
              <button className="mt-4 w-full py-2 bg-slate-700/50 text-slate-300 font-medium rounded-lg group-hover:bg-amber-500 group-hover:text-slate-900 transition-all flex items-center justify-center gap-2">
                Practice 10 <div className="w-4 h-4"><Icons.Zap /></div>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <div className="w-5 h-5 text-amber-400"><Icons.Zap /></div>Quick Practice
        </h2>
        <div className="grid grid-cols-4 gap-4">
          {[10, 25, 50].map(count => (
            <button key={count} onClick={() => onStartPractice(null, count)}
              className="p-4 bg-slate-700/50 rounded-xl hover:bg-amber-500/20 border border-transparent hover:border-amber-500/50 transition-all text-left group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center group-hover:bg-amber-500">
                  <div className="w-5 h-5 text-amber-400 group-hover:text-slate-900"><Icons.Shuffle /></div>
                </div>
                <div>
                  <h3 className="font-medium text-white">Mixed {count}</h3>
                  <p className="text-xs text-slate-400">Random topics</p>
                </div>
              </div>
            </button>
          ))}
          <button onClick={() => {
            const weakest = categoriesWithMastery.reduce((min, cat) => cat.mastery < min.mastery ? cat : min);
            onStartPractice(weakest, 15);
          }} className="p-4 bg-slate-700/50 rounded-xl hover:bg-rose-500/20 border border-transparent hover:border-rose-500/50 transition-all text-left group">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-500/20 rounded-lg flex items-center justify-center group-hover:bg-rose-500">
                <div className="w-5 h-5 text-rose-400 group-hover:text-slate-900"><Icons.Target /></div>
              </div>
              <div>
                <h3 className="font-medium text-white">Weakness</h3>
                <p className="text-xs text-slate-400">Target weak areas</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// QUESTION VIEW
// ============================================================================
const QuestionView = ({ question, questionIndex, totalQuestions, selectedAnswer, onSelectAnswer, onNext, onPrevious, showFeedback, mode, timeRemaining }) => {
  const isCorrect = selectedAnswer === question.correctIndex;
  const categoryNames = { A: 'Algebra', G: 'Geometry', F: 'Functions', S: 'Statistics', N: 'Number & Quantity', IES: 'IES' };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">Question {questionIndex + 1} of {totalQuestions}</span>
          {mode === 'testing' && timeRemaining !== undefined && (
            <span className={`flex items-center gap-1 text-sm ${timeRemaining < 300 ? 'text-rose-400' : 'text-slate-400'}`}>
              <div className="w-4 h-4"><Icons.Clock /></div>{formatTime(timeRemaining)}
            </span>
          )}
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all" style={{ width: `${((questionIndex + 1) / totalQuestions) * 100}%` }}/>
        </div>
      </div>

      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="px-2 py-1 bg-slate-700 text-slate-300 text-xs font-medium rounded-lg">{categoryNames[question.category] || question.category}</span>
          <span className="px-2 py-1 bg-slate-700 text-slate-300 text-xs font-medium rounded-lg">Difficulty: {question.difficulty}/3</span>
          <span className="px-2 py-1 bg-slate-700/50 text-slate-400 text-xs rounded-lg">{question.id}</span>
        </div>
        <h2 className="text-xl font-medium text-white mb-6 leading-relaxed">{question.question}</h2>
        <div className="space-y-3">
          {question.choices.map((choice, index) => {
            const letter = String.fromCharCode(65 + index);
            const isSelected = selectedAnswer === index;
            const isCorrectAnswer = index === question.correctIndex;
            let bgClass = 'bg-slate-700/50 border-slate-600 hover:border-amber-500/50';
            if (showFeedback) {
              if (isCorrectAnswer) bgClass = 'bg-emerald-500/20 border-emerald-500';
              else if (isSelected) bgClass = 'bg-rose-500/20 border-rose-500';
            } else if (isSelected) bgClass = 'bg-amber-500/20 border-amber-500';

            return (
              <button key={index} onClick={() => !showFeedback && onSelectAnswer(index)} disabled={showFeedback}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${bgClass} ${showFeedback ? 'cursor-default' : 'cursor-pointer'}`}>
                <div className="flex items-center gap-4">
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-medium ${
                    showFeedback && isCorrectAnswer ? 'bg-emerald-500 text-white' :
                    showFeedback && isSelected ? 'bg-rose-500 text-white' :
                    isSelected ? 'bg-amber-500 text-slate-900' : 'bg-slate-600 text-slate-300'
                  }`}>{letter}</span>
                  <span className="text-white">{choice}</span>
                  {showFeedback && isCorrectAnswer && <div className="w-5 h-5 text-emerald-400 ml-auto"><Icons.Check /></div>}
                  {showFeedback && isSelected && !isCorrectAnswer && <div className="w-5 h-5 text-rose-400 ml-auto"><Icons.X /></div>}
                </div>
              </button>
            );
          })}
        </div>

        {showFeedback && mode === 'practice' && (
          <div className={`mt-6 p-4 rounded-xl ${isCorrect ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-rose-500/10 border border-rose-500/30'}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-5 h-5 ${isCorrect ? 'text-emerald-400' : 'text-rose-400'}`}>{isCorrect ? <Icons.Check /> : <Icons.X />}</div>
              <span className={`font-medium ${isCorrect ? 'text-emerald-400' : 'text-rose-400'}`}>{isCorrect ? 'Correct!' : 'Incorrect'}</span>
            </div>
            <p className="text-slate-300">{question.explanation}</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6">
        <button onClick={onPrevious} disabled={questionIndex === 0} className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white disabled:opacity-50 transition-all">
          <div className="w-5 h-5"><Icons.ChevronLeft /></div>Previous
        </button>
        <button onClick={onNext} disabled={selectedAnswer === null && !showFeedback} className="flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed">
          {questionIndex === totalQuestions - 1 ? 'Finish' : 'Next'}<div className="w-5 h-5"><Icons.ChevronRight /></div>
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// RESULTS SCREEN
// ============================================================================
const ResultsScreen = ({ results, mode, onBack }) => {
  const correct = results.filter(r => r.correct).length;
  const percentage = Math.round((correct / results.length) * 100);
  const scaledScore = mode === 'testing' ? calculateScaledScore(correct) : null;
  const categoryNames = { A: 'Algebra', G: 'Geometry', F: 'Functions', S: 'Statistics', N: 'Number & Quantity', IES: 'IES' };

  const byCategory = results.reduce((acc, r) => {
    if (!acc[r.category]) acc[r.category] = { correct: 0, total: 0 };
    acc[r.category].total++;
    if (r.correct) acc[r.category].correct++;
    return acc;
  }, {});

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8 text-center">
        <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="w-10 h-10 text-amber-400"><Icons.Trophy /></div>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{mode === 'testing' ? 'Test Complete!' : 'Practice Complete!'}</h2>
        <p className="text-slate-400">{percentage >= 80 ? 'Excellent work!' : percentage >= 60 ? 'Good progress!' : 'Keep practicing!'}</p>
        
        <div className="grid grid-cols-3 gap-4 my-8">
          <div className="bg-slate-700/50 rounded-xl p-4">
            <p className="text-3xl font-bold text-white">{correct}</p>
            <p className="text-sm text-slate-400">Correct</p>
          </div>
          <div className="bg-slate-700/50 rounded-xl p-4">
            <p className="text-3xl font-bold text-white">{results.length - correct}</p>
            <p className="text-sm text-slate-400">Incorrect</p>
          </div>
          <div className="bg-slate-700/50 rounded-xl p-4">
            <p className="text-3xl font-bold text-amber-400">{scaledScore || `${percentage}%`}</p>
            <p className="text-sm text-slate-400">{mode === 'testing' ? 'Scaled Score' : 'Accuracy'}</p>
          </div>
        </div>

        <div className="text-left mb-8">
          <h3 className="text-lg font-semibold text-white mb-3">By Category</h3>
          <div className="space-y-2">
            {Object.entries(byCategory).map(([cat, data]) => (
              <div key={cat} className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3">
                <span className="text-slate-300">{categoryNames[cat] || cat}</span>
                <span className={`font-medium ${data.correct / data.total >= 0.8 ? 'text-emerald-400' : data.correct / data.total >= 0.5 ? 'text-amber-400' : 'text-rose-400'}`}>
                  {data.correct}/{data.total} ({Math.round(data.correct / data.total * 100)}%)
                </span>
              </div>
            ))}
          </div>
        </div>

        <button onClick={onBack} className="px-8 py-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-medium rounded-xl transition-all">Back to Dashboard</button>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN APP
// ============================================================================
export default function App() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('testing');
  const [view, setView] = useState('dashboard');
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [sessionResults, setSessionResults] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const [practiceHistory, setPracticeHistory] = useState([]);

  useEffect(() => {
    const savedTestHistory = localStorage.getItem('actPrep_testHistory');
    const savedPracticeHistory = localStorage.getItem('actPrep_practiceHistory');
    if (savedTestHistory) setTestHistory(JSON.parse(savedTestHistory));
    if (savedPracticeHistory) setPracticeHistory(JSON.parse(savedPracticeHistory));
  }, []);

  useEffect(() => {
    localStorage.setItem('actPrep_testHistory', JSON.stringify(testHistory));
  }, [testHistory]);

  useEffect(() => {
    localStorage.setItem('actPrep_practiceHistory', JSON.stringify(practiceHistory));
  }, [practiceHistory]);

  useEffect(() => {
    if (view === 'test' && timeRemaining !== null && timeRemaining > 0) {
      const timer = setInterval(() => setTimeRemaining(t => t - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [view, timeRemaining]);

  const handleLogin = (userData) => setUser(userData);
  const handleLogout = () => { setUser(null); setView('dashboard'); };

  const handleStartTest = (test) => {
    const questions = getRandomQuestions(test.scoredQuestions || test.questions);
    setCurrentQuestions(questions);
    setView('test');
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setSessionResults([]);
    setTimeRemaining(test.duration * 60);
  };

  const handleStartPractice = (category, count) => {
    const questions = category ? getRandomQuestions(count, category.id) : getRandomQuestions(count);
    setCurrentQuestions(questions);
    setView('practice');
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setSessionResults([]);
    setTimeRemaining(null);
  };

  const handleSelectAnswer = (index) => {
    setSelectedAnswer(index);
    if (view === 'practice') setShowFeedback(true);
  };

  const handleNext = () => {
    const q = currentQuestions[currentQuestion];
    const result = { questionId: q.id, category: q.category, selected: selectedAnswer, correct: selectedAnswer === q.correctIndex, timestamp: Date.now() };
    const newResults = [...sessionResults, result];
    setSessionResults(newResults);

    if (currentQuestion >= currentQuestions.length - 1) {
      if (view === 'test') {
        setTestHistory(prev => [...prev, { formId: 'practice', formName: 'Practice Test', date: Date.now(), rawScore: newResults.filter(r => r.correct).length, totalScored: newResults.length, scaledScore: calculateScaledScore(newResults.filter(r => r.correct).length), results: newResults }]);
      } else {
        setPracticeHistory(prev => [...prev, ...newResults]);
      }
      setView('results');
    } else {
      setCurrentQuestion(prev => prev + 1);
      setSelectedAnswer(null);
      setShowFeedback(false);
    }
  };

  const handlePrevious = () => { if (currentQuestion > 0) { setCurrentQuestion(prev => prev - 1); setSelectedAnswer(null); setShowFeedback(false); } };
  const handleBackToDashboard = () => { setView('dashboard'); setCurrentQuestions([]); setSessionResults([]); setTimeRemaining(null); };

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div className="min-h-screen bg-slate-900">
      <Header user={user} activeTab={activeTab} setActiveTab={(tab) => { setActiveTab(tab); setView('dashboard'); }} onLogout={handleLogout} />
      <main className="max-w-6xl mx-auto px-4 py-8">
        {view === 'dashboard' && activeTab === 'testing' && <TestingDashboard testHistory={testHistory} onStartTest={handleStartTest} />}
        {view === 'dashboard' && activeTab === 'practice' && <PracticeDashboard practiceHistory={practiceHistory} onStartPractice={handleStartPractice} />}
        {(view === 'test' || view === 'practice') && currentQuestions.length > 0 && (
          <QuestionView question={currentQuestions[currentQuestion]} questionIndex={currentQuestion} totalQuestions={currentQuestions.length} selectedAnswer={selectedAnswer} onSelectAnswer={handleSelectAnswer} onNext={handleNext} onPrevious={handlePrevious} showFeedback={showFeedback} mode={view === 'test' ? 'testing' : 'practice'} timeRemaining={timeRemaining} />
        )}
        {view === 'results' && <ResultsScreen results={sessionResults} mode={activeTab} onBack={handleBackToDashboard} />}
      </main>
    </div>
  );
}
